import type { ChatConfig, Message, UiTexts } from '../../types/chat'
import type { ResolvedPrivacyTexts } from '../../utils/privacy'
import ChatHeader from '../ChatHeader'
import ChatMessages from '../ChatMessages'
import ChatInput from '../ChatInput'
import ChatFooter from '../ChatFooter'
import PrivacyGate from '../PrivacyGate'
import styles from './ChatPanel.module.css'

interface Props {
  config: ChatConfig
  texts: UiTexts
  messages: Message[]
  isOpen: boolean
  isTyping: boolean
  onClose: () => void
  onSend: (text: string) => void
  showPrivacyGate: boolean
  privacyTexts: ResolvedPrivacyTexts
  onConsent: () => void
}

export default function ChatPanel({
  config,
  texts,
  messages,
  isOpen,
  isTyping,
  onClose,
  onSend,
  showPrivacyGate,
  privacyTexts,
  onConsent,
}: Props) {
  if (!isOpen) return null

  // Escape closes the chat from anywhere inside the panel.
  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      e.stopPropagation()
      onClose()
    }
  }

  return (
    <div
      id="n8n-chatbot-panel"
      className={styles.panel}
      data-part="panel"
      data-position={config.position ?? 'right'}
      data-dark={String(config.darkMode ?? false)}
      role="dialog"
      aria-label={texts.panelLabel}
      tabIndex={-1}
      onKeyDown={onKeyDown}
    >
      <ChatHeader config={config} texts={texts} onClose={onClose} />
      {showPrivacyGate ? (
        <PrivacyGate texts={privacyTexts} onAccept={onConsent} />
      ) : (
        <>
          <ChatMessages config={config} texts={texts} messages={messages} isTyping={isTyping} />
          <ChatInput config={config} texts={texts} onSend={onSend} />
        </>
      )}
      {config.showFooter !== false && <ChatFooter config={config} />}
    </div>
  )
}
